import React from "react";
import OrderSummary from "./OrderSummary";
import OrderTable from "./OrderTable";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import SearchInput from "../../../components/form/SearchInput";
import FilterByButton from "../../ui/FilterButton";
import { dispatchStatusFilter, orderStatusFilter } from "./util";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import { setCurrentTable } from "../../../redux/features/orderSlice";
import Button from "../../ui/Button";
import DateRangeSelector from "../../ui/DateRangeSelector";
import { useOrderFilters } from "./useOrderFilters";
import { canUseOrderManagementListFilters } from "../../../helpers";

const OrderManagement = () => {
  const {
    inputValue,
    setInputValue,
    commitSearch,
    search,
    status,
    setStatus,
    dispatchStatus,
    setDispatchStatus,
    dateRange,
    setDateRange,
    page,
    setPage,
    limit,
    setLimit,
    filterResetKey,
  } = useOrderFilters();

  const { currentTable, orders } = useAppSelector(
    (state: RootState) => state.orders,
  );
  const { user } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  const tabList = [
    { id: 1, name: "All Orders", count: orders?.data?.totalItems ?? 0 },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commitSearch();
  };

  return (
    <div className=" flex flex-col gap-4 sm:gap-4 p-4  sm:p-6 md:p-8 bg-gray-100 min-h-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold mb-2">Order Management</h1>
      </div>
      <OrderSummary />
      <WhiteTitledCard>
        <div className="px-6">
          <div className="flex md:items-center justify-between flex-col lg:flex-row mb-4 gap-6">
            {canUseOrderManagementListFilters(user) && (
              <DateRangeSelector
                key={`date-${filterResetKey}`}
                initialRange={dateRange}
                onChange={setDateRange}
              />
            )}
            <div className="flex-col md:flex-row md:items-center gap-4 flex">
              {canUseOrderManagementListFilters(user) && (
                <div className="flex gap-2">
                  <FilterByButton
                    key={`status-${filterResetKey}`}
                    options={orderStatusFilter}
                    onSelect={(value) => setStatus(value ?? "")}
                    buttonLabel="Order status"
                    defaultValue={status}
                  />
                  <FilterByButton
                    key={`dispatch-${filterResetKey}`}
                    options={dispatchStatusFilter}
                    onSelect={(value) => setDispatchStatus(value ?? "")}
                    buttonLabel="Dispatch status"
                    defaultValue={dispatchStatus}
                  />
                </div>
              )}

              <SearchInput
                placeholder="Search"
                onChange={(e: any) => setInputValue(e.target.value)}
                value={inputValue}
                onKeyDown={handleKeyDown}
              />
              <Button
                layout="toolbar" size="sm"
                label="filter"
                onClick={commitSearch}
                classNames="hidden sm:flex"
              />
            </div>
          </div>
          <div className="flex space-x-4 items-center">
            {tabList?.map((tab) => (
              <div
                key={tab.id}
                className={`space-x-2 cursor-pointer text-sm font-semibold p-2 ${
                  currentTable === tab.id
                    ? "text-black border-b-2 border-primary"
                    : ""
                }`}
                // Tab is a no-op switcher (only one tab today). Previously this
                // silently reset filters on click — surprising UX, especially
                // now that filters are URL-persistent. Tab click now only
                // updates the redux currentTable indicator.
                onClick={() => {
                  dispatch(setCurrentTable(tab.id));
                }}
              >
                <span>{tab.name}</span>
                {
                  <span
                    className={"bg-lightPurple px-2  text-accent rounded-md"}
                  >
                    {tab?.count}
                  </span>
                }
              </div>
            ))}
          </div>
        </div>
        <OrderTable
          status={status}
          search={search}
          filterByDispatch={dispatchStatus}
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          page={page}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </WhiteTitledCard>
    </div>
  );
};

export default OrderManagement;
